import React, { useState, useRef } from 'react';
import './Window.css';
import WindowHeader from './WindowHeader';

interface WindowProps {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  onFocus?: () => void;
  isFocused?: boolean;
  initialPosition?: { x: number; y: number };
  width?: number;
  height?: number;
  resizable?: boolean;
  onResize?: (width: number, height: number) => void;
  modal?: boolean;
}

const Window: React.FC<WindowProps> = ({
  title,
  children,
  onClose,
  onFocus,
  isFocused = false,
  initialPosition = { x: 100, y: 100 },
  width = 400,
  height = 300,
  resizable = false,
  onResize,
  modal = false
}) => {
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState({ width, height });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeCorner, setResizeCorner] = useState<'bottom-left' | 'bottom-right' | null>(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, posX: 0, posY: 0 });
  const [initialSize] = useState({ width, height }); // Store initial size for min constraint
  const windowRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    setIsDragging(true);
    onFocus?.();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setDragOffset({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
    setIsDragging(true);
    onFocus?.();
  };

  const handleWindowClick = () => {
    onFocus?.();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Add constraints to prevent dragging outside bounds
      const minX = -width + 100; // Allow partial off-screen to the left, but keep 100px visible
      const minY = 0; // Don't allow dragging above the top border
      const maxX = window.innerWidth - 100; // Keep 100px visible on the right
      const maxY = window.innerHeight - 50; // Keep title bar visible at bottom
      
      setPosition({
        x: Math.max(minX, Math.min(maxX, newX)),
        y: Math.max(minY, Math.min(maxY, newY))
      });
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging) {
      const touch = e.touches[0];
      const newX = touch.clientX - dragOffset.x;
      const newY = touch.clientY - dragOffset.y;
      
      // Add constraints to prevent dragging outside bounds
      const minX = -width + 100;
      const minY = 0;
      const maxX = window.innerWidth - 100;
      const maxY = window.innerHeight - 50;
      
      setPosition({
        x: Math.max(minX, Math.min(maxX, newX)),
        y: Math.max(minY, Math.min(maxY, newY))
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleResizeMouseDown = (corner: 'bottom-left' | 'bottom-right', e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeCorner(corner);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      posX: position.x,
      posY: position.y
    });
    onFocus?.();
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!isResizing || !resizeCorner) return;

    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;

    // Get viewport dimensions (95% max)
    const maxWidth = window.innerWidth * 0.95;
    const maxHeight = window.innerHeight * 0.95;

    // Min size is 50% of initial size, but at least 300x250 for file browsers
    const minWidth = Math.max(initialSize.width * 0.5, 300);
    const minHeight = Math.max(initialSize.height * 0.5, 250);

    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;
    let newPosX = position.x;

    if (resizeCorner === 'bottom-right') {
      // Bottom-right: top-left corner stays fixed, resize independently
      newWidth = resizeStart.width + deltaX;
      newHeight = resizeStart.height + deltaY;
    } else {
      // Bottom-left: top-right corner stays fixed, resize independently
      newWidth = resizeStart.width - deltaX;
      newHeight = resizeStart.height + deltaY;
      newPosX = resizeStart.posX + deltaX;
    }

    // Apply width constraints
    if (newWidth < minWidth) {
      newWidth = minWidth;
      if (resizeCorner === 'bottom-left') {
        newPosX = resizeStart.posX + (resizeStart.width - minWidth);
      }
    }

    if (newWidth > maxWidth) {
      newWidth = maxWidth;
      if (resizeCorner === 'bottom-left') {
        newPosX = resizeStart.posX + (resizeStart.width - maxWidth);
      }
    }

    // Apply height constraints
    if (newHeight < minHeight) {
      newHeight = minHeight;
    }

    if (newHeight > maxHeight) {
      newHeight = maxHeight;
    }

    setSize({ width: newWidth, height: newHeight });
    if (resizeCorner === 'bottom-left') {
      setPosition({ ...position, x: newPosX });
    }
    onResize?.(newWidth, newHeight);
  };

  const handleResizeUp = () => {
    setIsResizing(false);
    setResizeCorner(null);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragOffset]);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeUp);
    };
  }, [isResizing, resizeStart, resizeCorner]);

  return (
    <div
      className={`window ${isFocused ? 'focused' : ''} ${resizable ? 'resizable' : ''}`}
      ref={windowRef}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: modal ? 1000 : (isFocused ? 200 : 100)
      }}
      onClick={handleWindowClick}
    >
      <WindowHeader
        title={title}
        onClose={onClose}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      />
      <div className="window-content">
        {children}
      </div>
      {resizable && (
        <>
          <div
            className="resize-handle resize-handle-bottom-left"
            onMouseDown={(e) => handleResizeMouseDown('bottom-left', e)}
          />
          <div
            className="resize-handle resize-handle-bottom-right"
            onMouseDown={(e) => handleResizeMouseDown('bottom-right', e)}
          />
        </>
      )}
    </div>
  );
};

export default Window;